import React from 'react';
import * as d3 from 'd3';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './Chopsticks.css';

const xdim = 700;
const ydim = 700;
const aspect = xdim / 800;
const tooltipX = 80;
const legendX = 500;
const legendY = 20;
const legendSpacing = 18;
const paddingX = 50;
const paddingY = 50;
const innerXdim = 600;
const innerYdim = 600;
const regRadius = 7;
const bigRadius = 11;
const regOpacity = 0.4;
const bigOpacity = 0.7;
const extraOpacity = 1.0;
const circleYOffset = 4;
const colorDict = {
  winning: '#00aa55',
  draw: '#666666',
  losing: '#553399',
  lost: '#cc4444',
};
let count = -1;
const colorList = Object.keys(colorDict).map(key => {
  count++;
  return {
    type: key,
    color: colorDict[key],
    y: count * legendSpacing,
  };
});
const nodeSelectionColor = '#000';
const childSelections = [
  { color: '#333', y: 2 * legendSpacing },
  { color: '#777', y: 3 * legendSpacing },
  { color: '#aaa', y: 4 * legendSpacing },
  { color: '#ddd', y: 5 * legendSpacing },
];
const selectionWidth = 3;

function formatState(d) {
  return `${d.p0} vs ${d.p1}`;
}

function dist(dx, dy) {
  return aspect * Math.sqrt(dx * dx + dy * dy);
}

function fx(v1, v2) {
  const dx = v2.x - v1.x;
  const dy = v2.y - v1.y;
  const dd = dist(dx, dy);
  return v1.x * aspect + bigRadius * dx / dd;
}

function fy(v1, v2) {
  const dx = v2.x - v1.x;
  const dy = v2.y - v1.y;
  const dd = dist(dx, dy);
  return v1.y * aspect + bigRadius * dy / dd;
}

class Chopsticks extends React.Component {
  componentDidMount() {
    const svg = d3.select('#graph1').append('svg')
      .attr('width', xdim)
      .attr('height', ydim)
      .append('g')
      .attr('transform', `translate(${paddingX},${paddingY})`)
      .attr('width', innerXdim)
      .attr('height', innerYdim);
    const tooltip = svg.append('g')
      .attr('transform', `translate(${tooltipX},${legendY})`)
      .attr('class', s.tooltip);
    const legend = svg.append('g')
      .attr('transform', `translate(${legendX},${legendY})`)
      .attr('class', s.legend);
    legend.selectAll('text')
      .data(colorList)
      .enter()
      .append('text')
      .style('opacity', 1)
      .text((d) => d.type)
      .attr('x', legendSpacing)
      .attr('y', (d) => d.y);

    svg.append('svg:defs')
      .append('svg:marker')
      .attr('id', 'arrow')
      .attr('viewBox', '-10, -5, 10, 10')
      .attr('markerWidth', 10)
      .attr('markerHeight', 10)
      .attr('orient', 'auto')
      .append('svg:path')
      .attr('d', 'M-10,-4 L0,0 L-10,4');

    d3.json('/posts/chopsticks/nodes.json', (json) => {
      const nodePropDict = {};
      json.nodes.forEach((d) => {
        nodePropDict[d.id] = d;
      });

      const nodes = svg.selectAll('circle')
        .data(json.nodes)
        .enter()
        .append('circle');

      const childDict = {};
      const nodeDict = {};
      const edgeDict = {};
      const allEdgeData = [];
      json.nodes.forEach((d) => {
        childDict[d.id] = nodes.filter((y) => (d.children.indexOf(y.id) !== -1));
        nodeDict[d.id] = nodes.filter((y) => (d.id === y.id));
        allEdgeData.push(...d.children);
        const newEdges = svg.selectAll('line')
          .data(allEdgeData)
          .enter()
          .append('line')
          .attr('stroke', '#aaaaaa')
          .attr('stroke-width', 1)
          .attr('marker-end', 'url(#arrow)')
          .style('opacity', regOpacity)
          .style('pointer-events', 'none');
        edgeDict[d.id] = newEdges;
      });
      let oldNode = svg.select('circle');
      let oldChildren = svg.select('circle');
      let oldEdges = svg.select('line');

      json.nodes.forEach((d) => {
        edgeDict[d.id]
          .attr('x1', (e) => fx(d, nodePropDict[e]))
          .attr('y1', (e) => fy(d, nodePropDict[e]))
          .attr('x2', (e) => fx(nodePropDict[e], d))
          .attr('y2', (e) => fy(nodePropDict[e], d));
      });

      nodes
        .attr('cx', (d) => d.x * aspect)
        .attr('cy', (d) => d.y * aspect)
        .attr('r', regRadius)
        .style('stroke-width', selectionWidth)
        .style('fill', (d) => colorDict[d.type])
        .style('opacity', regOpacity)
        .on('click', (d) => {
          oldNode
            .style('opacity', regOpacity)
            .style('stroke', 'none')
            .attr('r', regRadius);
          oldChildren
            .style('opacity', regOpacity)
            .style('stroke', 'none')
            .attr('r', regRadius);
          oldEdges
            .style('opacity', regOpacity);

          nodeDict[d.id]
            .style('opacity', extraOpacity)
            .style('stroke', nodeSelectionColor)
            .attr('r', bigRadius);
          childDict[d.id]
            .style('opacity', bigOpacity)
            .attr('r', bigRadius);
          for (let i = 0; i < d.children.length; i++) {
            nodeDict[d.children[i]]
              .style('stroke', childSelections[i].color);
          }
          edgeDict[d.id]
            .style('opacity', extraOpacity);

          oldNode = nodeDict[d.id];
          oldChildren = childDict[d.id];
          oldEdges = edgeDict[d.id];

          tooltip.selectAll('text')
            .remove();
          tooltip.selectAll('circle')
            .remove();
          tooltip.append('text')
            .attr('x', legendSpacing)
            .attr('fill', colorDict[d.type])
            .text(formatState(d));
          tooltip.append('circle')
            .style('stroke', nodeSelectionColor)
            .style('stroke-width', selectionWidth)
            .style('fill', 'none')
            .attr('cy', -circleYOffset)
            .attr('r', regRadius);
          tooltip.append('text')
            .attr('x', legendSpacing)
            .attr('y', legendSpacing)
            .text('Moves:');
          for (let i = 0; i < d.children.length; i++) {
            const child = nodePropDict[d.children[i]];
            tooltip.append('text')
              .attr('x', legendSpacing)
              .attr('y', legendSpacing * (i + 2))
              .attr('fill', colorDict[child.type])
              .text(formatState(child));
            tooltip.append('circle')
              .style('stroke', childSelections[i].color)
              .style('stroke-width', selectionWidth)
              .style('fill', 'none')
              .style('opacity', bigOpacity)
              .attr('cy', childSelections[i].y - circleYOffset)
              .attr('r', regRadius);
          }
        });
      legend.selectAll('circle')
          .data(colorList)
          .enter()
          .append('circle')
          .attr('fill', (d) => d.color)
          .attr('cy', (d) => d.y - circleYOffset)
          .attr('r', regRadius);
    });
  }

  render() {
    return (
      <div id="graph1" />
    );
  }
}

export default withStyles(s)(Chopsticks);



// WEBPACK FOOTER //
// src/components/Chopsticks/Chopsticks.js